/**
 * ToolCallPopup — popup compartilhado de detalhes de tool call.
 * Usado por ExecModal (logs de execução) e ConversationModal (chat).
 */

import { useState } from "react";
import { DialogPanel } from "./DialogPanel";
import { backdropClose } from "./backdrop";

export interface ToolCall {
  kind?: "http" | "python";
  name: string;
  arguments: Record<string, unknown>;
  result: string;
  logs?: string | null;
  method?: string;
  url?: string;
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  body?: Record<string, unknown>;
  body_type?: string;
}

export function buildCurl(tc: ToolCall): string {
  if (tc.kind === "python" || !tc.method || !tc.url) return "";
  const lines: string[] = [`curl -X ${tc.method} "${tc.url}"`];
  if (tc.headers) {
    for (const [k, v] of Object.entries(tc.headers)) {
      lines.push(`  -H "${k}: ${v}"`);
    }
  }
  if (tc.body_type === "form" && tc.body) {
    for (const [k, v] of Object.entries(tc.body)) {
      lines.push(`  -F "${k}=${v}"`);
    }
  } else if (tc.body) {
    lines.push(`  -H "Content-Type: application/json"`);
    lines.push(`  -d '${JSON.stringify(tc.body)}'`);
  } else if (tc.params) {
    const qs = new URLSearchParams(
      Object.entries(tc.params).map(([k, v]) => [k, String(v)])
    ).toString();
    if (qs) lines[0] = `curl -X ${tc.method} "${tc.url}?${qs}"`;
  }
  return lines.join(" \\\n");
}

export function ToolCallPopup({ tc, onClose }: { tc: ToolCall; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const curl = buildCurl(tc);
  function copy() {
    navigator.clipboard.writeText(curl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <div
      {...backdropClose(() => { onClose(); })}
      style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "calc(var(--z-modal) + 200)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <DialogPanel onClose={() => onClose()} label={`Tool: ${tc.name}`} style={{ background: "var(--surface-hover)", border: "1px solid var(--line-strong)", borderRadius: 12, width: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(16,16,20,0.14)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid var(--line)", flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Tool: <span style={{ color: "var(--brand)" }}>{tc.name}</span></span>
          <button onClick={onClose} style={{ color: "var(--ink-muted)", background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 4 }}
            onMouseOver={e => (e.currentTarget.style.color = "var(--ink)")} onMouseOut={e => (e.currentTarget.style.color = "var(--ink-muted)")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px", display: "flex", flexDirection: "column", gap: 14 }}>
          {Object.keys(tc.arguments ?? {}).length > 0 && (
            <div>
              <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>Argumentos (entrada)</div>
              <pre style={{ margin: 0, fontSize: 11, color: "var(--ink-mid)", whiteSpace: "pre-wrap", fontFamily: "var(--font-mono)", lineHeight: 1.6 }}>
                {JSON.stringify(tc.arguments, null, 2)}
              </pre>
            </div>
          )}
          {tc.result && (
            <div>
              <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>Resposta da API</div>
              <pre style={{ margin: 0, fontSize: 11, color: "var(--ink-strong)", whiteSpace: "pre-wrap", fontFamily: "var(--font-mono)", lineHeight: 1.6 }}>{tc.result}</pre>
            </div>
          )}
          {tc.kind === "python" && tc.logs && (
            <div>
              <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>Logs (Python)</div>
              <pre style={{ margin: 0, fontSize: 11, color: "var(--ink-mid)", whiteSpace: "pre-wrap", fontFamily: "var(--font-mono)", lineHeight: 1.6 }}>{tc.logs}</pre>
            </div>
          )}
          {curl && (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 10, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>cURL</span>
                <button onClick={copy} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, border: "1px solid var(--line-strong)", background: "var(--line)", color: copied ? "var(--ok)" : "var(--ink-secondary)", cursor: "pointer" }}>
                  {copied ? "✓ Copiado" : "Copiar"}
                </button>
              </div>
              <pre style={{ margin: 0, fontSize: 11, color: "var(--info)", whiteSpace: "pre-wrap", fontFamily: "var(--font-mono)", lineHeight: 1.6 }}>{curl}</pre>
            </div>
          )}
        </div>
      </DialogPanel>
    </div>
  );
}
